import useSWR from "swr";
import { RefreshButton } from "@/components/list/refresh-button";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { Section } from "@/components/section";
import { fetcher } from "@/lib/swr";
import type { Subscription } from "@/components/webhooks/webhook-events";
import { WebhookDialog } from "@/components/webhooks/webhook-dialog";
import { SubscriptionRow } from "@/components/webhooks/subscription-row";

// Admin-only outbound webhook subscriptions (see server/docs/webhooks.md).
// Full list across apps — every subscription is bound to one app (global
// subscriptions were dropped in v2); app owners manage their own app's
// subscriptions from App Settings → Webhooks instead.
const Webhooks = () => {
  const {
    data: subs,
    isLoading,
    isValidating,
    mutate: refresh,
  } = useSWR<Subscription[]>(["/webhooks"], fetcher);
  const list = subs ?? [];

  return (
    <Section
      title="Webhook subscriptions"
      action={
        <div className="flex items-center gap-2">
          <RefreshButton spinning={isValidating} onClick={() => refresh()} />
          <WebhookDialog onSaved={() => refresh()} apiBase="/webhooks" />
        </div>
      }
    >
      <p className="text-xs text-muted-foreground">
        Push-only callbacks signed with the subscriber&apos;s secret
        (HMAC-SHA256). Fire-and-forget: no retries — see server/docs/webhooks.md
        for payload, verification and loop-avoidance.
      </p>
      <div className="flex flex-col gap-1">
        {isLoading && <ListLoading />}
        {!isLoading && list.length === 0 && (
          <ListEmpty>No webhook subscriptions.</ListEmpty>
        )}
        {list.map((sub, i) => (
          <SubscriptionRow
            key={sub.id}
            sub={sub}
            index={i}
            onChanged={() => refresh()}
            apiBase="/webhooks"
          />
        ))}
      </div>
    </Section>
  );
};

export default Webhooks;
